import React from 'react';
import { EditIcon3, DeleteIcon } from 'assets';
import { JobCriteriaData } from '../types';
import { PersonalityAlignmentsEditModal, DeleteSectionModal } from 'components';
import { useToast, usePersonalityQualificationEdit, usePersonalityQualificationDelete } from 'hooks';
import { PersonalityQualificationPayload } from 'hooks';

interface PersonalityAlignmentsSectionProps {
  assessmentId: string | null;
  data: JobCriteriaData['personalityAlignments'];
  onEdit: () => void;
  onDelete: () => void;
  onSave: (updatedData: JobCriteriaData['personalityAlignments']) => void;
  disabled?: boolean;
}

const PersonalityAlignmentsSection: React.FC<PersonalityAlignmentsSectionProps> = ({
  assessmentId,
  data,
  onEdit,
  onDelete,
  onSave,
  disabled
}) => {
  const { openToast } = useToast();
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const {
    submitPersonalityQualification,
    isSubmitting
  } = usePersonalityQualificationEdit(assessmentId);
  const {
    deletePersonalityQualification,
  } = usePersonalityQualificationDelete(assessmentId);

  const onSubmit = (formData: JobCriteriaData['personalityAlignments']) => {
    if (!assessmentId || isSubmitting) {
      openToast({
        show: true,
        heading: "Required",
        text: "Assessment ID is required",
        type: "error",
        timeOut: 5000,
      });
      return;
    }

    const payload: PersonalityQualificationPayload = {
      assessment_id: assessmentId,
      personalityAlignments: formData.personalityAlignments.reduce((acc, alignment, index) => ({
        ...acc,
        [`quality${index + 1}`]: alignment.category,
        [`description${index + 1}`]: alignment.description,
        [`most_prefered${index + 1}`]: alignment.mostPreferred || '',
        [`next_prefered${index + 1}`]: alignment.nextPreferred || '',
        [`least_prefered${index + 1}`]: alignment.leastPreferred || ''
      }), {})
    };

    submitPersonalityQualification(payload, {
      onSuccess: () => {
        onSave(formData);
        setIsEditModalOpen(false);
      },
      onError: (_err) => {
        setIsEditModalOpen(false);
      },
    });
  };

  const handleDeleteConfirm = () => {
    if (!assessmentId) {
      openToast({
        show: true,
        heading: "Required",
        text: "Assessment ID is required for deletion",
        type: "error",
        timeOut: 5000,
      });
      return;
    }

    deletePersonalityQualification({ assessment_id: assessmentId }, {
      onSuccess: () => {
        onDelete();
        setIsDeleteModalOpen(false);
      },
    });
  };

  return (
    <section className="relative">
      <div className="flex justify-between items-center mb-4 border-t border-b border-[#dfe1e6] py-6">
        <h2 className="text-xl sm:text-2xl font-medium sm:font-semibold m-0">Personality alignment</h2>
        {!disabled && (
          <div className="flex gap-3.5 items-center">
            <span className="cursor-pointer">
              <EditIcon3 onClick={() => { onEdit(); setIsEditModalOpen(true); }} />
            </span>
            <div className="relative group cursor-pointer">
              <DeleteIcon onClick={() => setIsDeleteModalOpen(true)} />
              <div className="absolute bottom-full right-0 mb-2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-52 z-10">
                <span className="block bg-[#1d2939] text-white text-xs rounded-lg px-3.5 py-2.5 leading-relaxed">
                  You can delete section you don't want to screen at this stage
                </span>
                <span className="absolute top-full right-[18px] w-0 h-0 border-x-[6px] border-x-transparent border-t-[6px] border-t-[#1d2939]" />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col">
        {data.personalityAlignments.map((alignment, index) => (
          <div key={index} className="mb-1">
            <h3 className="text-lg text-[#1D2939] mb-2 font-bold">{alignment.category}</h3>
            <p className="font-normal mb-2.5 text-[#1D2939] text-sm">{alignment.description}</p>
            <div className="flex flex-col gap-2">
              <ul className="p-0 m-0">
                {alignment.mostPreferred && (
                  <li className="list-none font-semibold"> {alignment.mostPreferred}
                  </li>
                )}
                {alignment.mostPreferredDescription && (
                  <li className="list-none pl-1.5 mb-2.5">
                    <span className="text-[#1D2939] text-sm mr-1">●</span>
                    <span className="text-[#1D2939]">{alignment.mostPreferredDescription}</span>
                  </li>
                )}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {!disabled && (
        <p className="border-t border-[#D0D5DD] border-b pt-6 pb-6 text-[15px] text-[#505f79]">
          You can adjust or remove any of these using the editing and delete button respectively
        </p>
      )}

      <PersonalityAlignmentsEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        currentData={data}
        onSave={onSubmit}
        isSubmitting={isSubmitting}
      />

      <DeleteSectionModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        sectionName="Personality Alignment"
      />
    </section>
  );
};

export default PersonalityAlignmentsSection;
